import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../utils/api';
import { API_BASE_URL } from '../config';
import { Role } from '../types/enums';
import { normalizeKegCode, isValidKegCode } from '../utils/kegCode';

export type KegTag = {
  code: string;
  status: string | null;
  locationId: number | null;
  locationName: string | null;
  lastScanned: string | null;
  onLine?: boolean;
};

export type KegTagGroup = {
  lineId?: number | null;
  itemName: string;
  productName: string;
  packageType: string;
  quantity: number;
  source: string | null;
  tags: KegTag[];
};

export type KegTagsPayload = {
  kind: 'invoice' | 'sales_order';
  invoiceId?: number | null;
  invoiceNumber?: string | null;
  orderId?: number | null;
  customerName?: string | null;
  groups: KegTagGroup[];
  tagCount: number;
  readOnly?: boolean;
  canAttach?: boolean;
};

type Mismatch = {
  code: string;
  reason?: string;
  message?: string;
  kegProduct?: string | null;
  kegPackage?: string | null;
  lineItem?: string;
};

type Props = {
  /** API path after /api, e.g. invoices/2878/keg-tags */
  apiPath: string;
  /** Breadcrumb parent label, e.g. Invoices or Sales Orders */
  parentLabel: string;
  parentTo: string;
  /** Current document label, e.g. ATH-1058 or Sales Order 42 */
  documentLabel: string;
  onClose: () => void;
};

function sourceLabel(source: string | null): string | null {
  if (!source) return null;
  if (source === 'line') return 'From order/invoice lines';
  if (source === 'keg_transactions.invoiceId') return 'From keg history (invoice link)';
  if (source === 'keg_transactions.text') return 'From keg history (invoice/TO ref in notes)';
  if (source.startsWith('keg_transactions')) return 'From keg history';
  return null;
}

function groupHeading(g: KegTagGroup): string {
  if (g.productName && g.packageType) return `${g.productName} ${g.packageType}`;
  return g.itemName || g.productName || 'Kegs';
}

function currentUserIsAdmin(): boolean {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return u?.role === Role.Admin || u?.role === Role.SuperAdmin;
  } catch {
    return false;
  }
}

const KegTagsPanel: React.FC<Props> = ({
  apiPath,
  parentLabel,
  parentTo,
  documentLabel,
  onClose,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payload, setPayload] = useState<KegTagsPayload | null>(null);
  const [busy, setBusy] = useState(false);
  const [attachLineId, setAttachLineId] = useState<number | null>(null);
  const [attachInput, setAttachInput] = useState('');
  const [pendingMismatch, setPendingMismatch] = useState<{
    lineId: number;
    codes: string[];
    mismatches: Mismatch[];
  } | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const isAdmin = currentUserIsAdmin();
  const canAttach = !!(payload?.canAttach ?? isAdmin);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/${apiPath}`, {
        headers: { Accept: 'application/json' },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      setPayload(body as KegTagsPayload);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load keg tags');
    } finally {
      setLoading(false);
    }
  }, [apiPath]);

  useEffect(() => {
    void load();
  }, [load]);

  const editableGroups = (payload?.groups || []).filter(
    (g) => g.lineId != null && Number(g.lineId) > 0
  );

  const postLink = async (
    action: 'attach' | 'detach',
    body: Record<string, unknown>
  ): Promise<{ ok: true; body: KegTagsPayload & { mismatches?: Mismatch[] } } | { ok: false; status: number; body: any }> => {
    const res = await apiFetch(`${API_BASE_URL}/api/${apiPath}/${action}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, status: res.status, body: data };
    return { ok: true, body: data };
  };

  const runAttach = async (lineId: number, codes: string[], allowMismatch = false) => {
    setBusy(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const result = await postLink('attach', { lineId, codes, allowMismatch });
      if (!result.ok) {
        if (result.status === 409 && result.body?.code === 'KEG_MISMATCH') {
          setPendingMismatch({
            lineId,
            codes,
            mismatches: result.body.mismatches || [],
          });
          return;
        }
        throw new Error(result.body?.error || `Attach failed (HTTP ${result.status})`);
      }
      setPayload(result.body);
      setPendingMismatch(null);
      setAttachInput('');
      setAttachLineId(null);
      setSuccessMessage(
        `Attached ${codes.join(', ')}. Inventory On Hand, invoice totals, and keg status were not changed.`
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Attach failed');
    } finally {
      setBusy(false);
    }
  };

  const runDetach = async (lineId: number, code: string) => {
    setBusy(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const result = await postLink('detach', { lineId, codes: [code] });
      if (!result.ok) {
        throw new Error(result.body?.error || `Detach failed (HTTP ${result.status})`);
      }
      setPayload(result.body);
      setSuccessMessage(`Detached ${code} from this line link only.`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Detach failed');
    } finally {
      setBusy(false);
    }
  };

  const submitAttach = () => {
    if (attachLineId == null) {
      setError('Choose a line to attach kegs to');
      return;
    }
    const codes = attachInput
      .split(/[\s,;]+/)
      .map((c) => normalizeKegCode(c))
      .filter((c) => c && isValidKegCode(c));
    if (!codes.length) {
      setError('Enter one or more valid keg codes (e.g. EK09821)');
      return;
    }
    void runAttach(attachLineId, codes, false);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 2000,
        padding: '16px',
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="keg-tags-title"
      data-testid="keg-tags-panel"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          padding: '20px',
          borderRadius: '8px',
          width: 'min(560px, 100%)',
          maxHeight: '85vh',
          overflow: 'auto',
          boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
        }}
      >
        <p
          style={{
            margin: '0 0 8px',
            fontSize: '13px',
            color: '#555',
          }}
          data-testid="keg-tags-breadcrumb"
        >
          <Link to={parentTo} style={{ color: '#1a5f2a' }}>
            {parentLabel}
          </Link>
          <span aria-hidden="true"> › </span>
          <span>{documentLabel}</span>
          <span aria-hidden="true"> › </span>
          <span>Keg Tags</span>
        </p>
        <h3
          id="keg-tags-title"
          style={{ color: '#555', margin: '0 0 12px', fontSize: '20px' }}
        >
          Keg Tags
        </h3>
        <p style={{ color: '#666', fontSize: '13px', margin: '0 0 16px' }}>
          {canAttach
            ? 'Lists keg codes tied to this document. Attach/detach updates the line link only — not inventory On Hand, invoice totals, or keg status/location.'
            : 'Read-only list of keg codes tied to this document. Does not change inventory or keg status.'}
        </p>

        {loading && <p data-testid="keg-tags-loading">Loading…</p>}
        {error && (
          <div className="error" data-testid="keg-tags-error">
            {error}
          </div>
        )}
        {successMessage && (
          <div
            data-testid="keg-tags-success"
            style={{ color: '#166534', backgroundColor: '#e6ffe6', padding: '8px', marginBottom: '12px' }}
          >
            {successMessage}
          </div>
        )}

        {!loading && !error && payload && (
          <>
            {payload.tagCount === 0 && editableGroups.length === 0 ? (
              <p data-testid="keg-tags-empty" style={{ color: '#555' }}>
                No keg tags found on lines or in keg history for this document.
              </p>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {(payload.groups.length ? payload.groups : editableGroups).map((g) => (
                  <li
                    key={`${g.lineId || 'x'}|${g.itemName}|${g.packageType}`}
                    style={{
                      marginBottom: '16px',
                      borderBottom: '1px solid #eee',
                      paddingBottom: '12px',
                    }}
                    data-testid={`keg-tags-group-${groupHeading(g)}`}
                  >
                    <div style={{ fontWeight: 600, color: '#333', marginBottom: '6px' }}>
                      {groupHeading(g)}
                      <span style={{ fontWeight: 400, color: '#666' }}>
                        {' '}
                        ({g.tags.length}
                        {g.quantity ? ` of ${g.quantity}` : ''})
                      </span>
                    </div>
                    {sourceLabel(g.source) && (
                      <div style={{ fontSize: '12px', color: '#777', marginBottom: '6px' }}>
                        {sourceLabel(g.source)}
                      </div>
                    )}
                    {g.tags.length === 0 ? (
                      <div style={{ color: '#888', fontSize: '14px' }}>No codes found</div>
                    ) : (
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {g.tags.map((t) => (
                          <li
                            key={t.code}
                            style={{
                              marginBottom: '4px',
                              fontSize: '15px',
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'center',
                              gap: '8px',
                            }}
                          >
                            <span>
                              <Link
                                to={`/keg-tracking?keg=${encodeURIComponent(t.code)}`}
                                data-testid={`keg-tags-code-${t.code}`}
                                style={{ color: '#1a5f2a', fontWeight: 600 }}
                              >
                                {t.code}
                              </Link>
                              {t.status ? (
                                <span style={{ color: '#444' }}> · {t.status}</span>
                              ) : null}
                              {t.locationName ? (
                                <span style={{ color: '#666' }}> · {t.locationName}</span>
                              ) : null}
                            </span>
                            {canAttach && g.lineId != null && (
                              <button
                                type="button"
                                data-testid={`keg-tags-detach-${t.code}`}
                                disabled={busy}
                                onClick={() => void runDetach(Number(g.lineId), t.code)}
                                style={{
                                  backgroundColor: '#fff',
                                  color: '#B91C1C',
                                  border: '1px solid #B91C1C',
                                  borderRadius: '4px',
                                  padding: '4px 10px',
                                  cursor: busy ? 'wait' : 'pointer',
                                  fontSize: '13px',
                                  minHeight: '36px',
                                }}
                              >
                                Detach
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                    {g.tags.length > 0 && (
                      <div
                        style={{ marginTop: '6px', fontSize: '13px', color: '#555' }}
                        data-testid={`keg-tags-codes-line-${groupHeading(g)}`}
                      >
                        {groupHeading(g)}: {g.tags.map((t) => t.code).join(', ')}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {canAttach && editableGroups.length > 0 && (
              <div
                data-testid="keg-tags-attach-form"
                style={{
                  marginTop: '8px',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: '#fafafa',
                }}
              >
                <div style={{ fontWeight: 600, color: '#333', marginBottom: '8px' }}>
                  Attach keg codes
                </div>
                <label style={{ display: 'block', fontSize: '13px', color: '#555', marginBottom: '4px' }}>
                  Line
                </label>
                <select
                  data-testid="keg-tags-attach-line"
                  value={attachLineId ?? ''}
                  onChange={(e) =>
                    setAttachLineId(e.target.value ? Number(e.target.value) : null)
                  }
                  style={{ width: '100%', padding: '8px', marginBottom: '8px' }}
                  disabled={busy}
                >
                  <option value="">Select line</option>
                  {editableGroups.map((g) => (
                    <option key={g.lineId!} value={g.lineId!}>
                      {groupHeading(g)}
                    </option>
                  ))}
                </select>
                <label style={{ display: 'block', fontSize: '13px', color: '#555', marginBottom: '4px' }}>
                  Keg codes (comma or space separated)
                </label>
                <input
                  type="text"
                  data-testid="keg-tags-attach-input"
                  value={attachInput}
                  onChange={(e) => setAttachInput(e.target.value)}
                  placeholder="EK09821 EK09972 …"
                  style={{ width: '100%', padding: '8px', marginBottom: '8px', boxSizing: 'border-box' }}
                  disabled={busy}
                />
                <button
                  type="button"
                  data-testid="keg-tags-attach-submit"
                  onClick={submitAttach}
                  disabled={busy}
                  style={{
                    backgroundColor: '#1a5f2a',
                    color: '#fff',
                    padding: '10px 16px',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: busy ? 'wait' : 'pointer',
                    minHeight: '44px',
                    fontWeight: 600,
                  }}
                >
                  {busy ? 'Saving…' : 'Attach'}
                </button>
              </div>
            )}
          </>
        )}

        {pendingMismatch && (
          <div
            data-testid="keg-tags-mismatch-warn"
            style={{
              marginTop: '12px',
              padding: '12px',
              border: '1px solid #92400E',
              backgroundColor: '#FFFBEB',
              borderRadius: '4px',
            }}
          >
            <div style={{ fontWeight: 600, color: '#92400E', marginBottom: '8px' }}>
              Product/package mismatch
            </div>
            <ul style={{ margin: '0 0 12px', paddingLeft: '18px', color: '#444', fontSize: '14px' }}>
              {pendingMismatch.mismatches.map((m) => (
                <li key={m.code}>{m.message || `${m.code} does not match this line`}</li>
              ))}
            </ul>
            <p style={{ fontSize: '13px', color: '#555', marginBottom: '12px' }}>
              Attach anyway? This still only writes the line link — not inventory or keg status.
            </p>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                type="button"
                data-testid="keg-tags-mismatch-override"
                disabled={busy}
                onClick={() =>
                  void runAttach(pendingMismatch.lineId, pendingMismatch.codes, true)
                }
                style={{
                  backgroundColor: '#92400E',
                  color: '#fff',
                  padding: '8px 14px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: busy ? 'wait' : 'pointer',
                  minHeight: '44px',
                }}
              >
                Attach anyway
              </button>
              <button
                type="button"
                data-testid="keg-tags-mismatch-cancel"
                disabled={busy}
                onClick={() => setPendingMismatch(null)}
                style={{
                  backgroundColor: '#6B7280',
                  color: '#fff',
                  padding: '8px 14px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  minHeight: '44px',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            data-testid="keg-tags-close"
            disabled={busy}
            style={{
              backgroundColor: '#6B7280',
              color: '#fff',
              padding: '10px 18px',
              border: 'none',
              borderRadius: '4px',
              cursor: busy ? 'wait' : 'pointer',
              minHeight: '44px',
              fontSize: '16px',
              fontWeight: 600,
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default KegTagsPanel;
